// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useContext, useState, useEffect } from 'react';
import {
    FormRow,
    ValidInput,
    Tile,
    Form,
    CheckboxGroup,
    Checkbox,
    Expander,
    ExpanderList,
    InputError,
    Alert,
    PopupHint,
} from '@op/opds.opux';
import message from '../../../messages';
import Roles from './Roles';
import {
    isValidAdminSelectedServices,
    isValidEmailInput,
    isValidFinnishSSN,
    isValidPhoneInput,
    isValidTextInput,
} from '../../../validation/Validation';
import { ContextWrapper, AdminData, Role } from '../../../context/ContextWrapper';
import AdminInformationFormHeader from './AdminInformationFormHeader';
import DigitalAgreementInformation from '../DigitalAgreementInformation';
import { id, companyTypes } from '../../../Constants';
import { deepCopy, getRolesForAgreementType, sortByAgreementType } from '../../../util/Util';
import { breakpoint } from '@op/opds.tokens';

interface AdminInformationFormProps {
    admin: AdminData;
    updateInformation: any;
    enableDeleteButton: boolean;
    removeForm: () => void;
}

const AdminInformationForm = React.forwardRef<HTMLDivElement, AdminInformationFormProps>(
    ({ admin, updateInformation, enableDeleteButton, removeForm }, ref) => {
        const { contractData, isAdminFlow } = useContext(ContextWrapper);
        const isAdminFormDisabled: boolean =
            isAdminFlow || !contractData.showAgreementTypes ? false : !contractData.companyData.companyTypes.length;
        const checkTheValidation: boolean = contractData.checkValidation;
        const adminData: AdminData = deepCopy(admin);

        const firstNameValidation = isValidTextInput(adminData.firstName, checkTheValidation);
        const lastNameValidation = isValidTextInput(adminData.lastName, checkTheValidation);
        const identityNumberValidation = isValidFinnishSSN(adminData.personalIdentityCode, checkTheValidation);
        const phoneNumberValidation = isValidPhoneInput(adminData.adminPhoneNumber, checkTheValidation);
        const emailValidation = isValidEmailInput(adminData.adminEmailAddress, checkTheValidation);
        const adminsSelectedServices = isValidAdminSelectedServices(adminData.agreementTypes, checkTheValidation);

        const [isMobile, setIsMobile] = useState<boolean>(false);

        useEffect(() => {
            const widthThreshold = breakpoint.xs.max;
            const mediaQuery = window.matchMedia(`(max-width: ${widthThreshold})`);
            mediaQuery.onchange = (e: MediaQueryListEvent) => {
                setIsMobile(e.matches);
            };

            return () => {
                mediaQuery.onchange = null;
            };
        }, []);

        const getCompanyTypes = (adminFlow: boolean, showAgreementTypes: boolean): readonly string[] => {
            if (adminFlow && showAgreementTypes) {
                return companyTypes;
            } else if (!showAgreementTypes) {
                return ['company'];
            }
            return contractData?.companyData?.companyTypes || [];
        };

        const updateAdminAgreements = (type: string, isChecked: boolean): void => {
            if (isChecked) {
                adminData.agreementTypes[type] = {
                    rolesData: {
                        roles: getRolesForAgreementType(type).map((role: Role) => {
                            return { ...role, selectedForAgreementType: type, isChecked: true };
                        }),
                        allRolesSelected: true,
                    },
                };
            } else {
                delete adminData.agreementTypes[type];
            }
            updateInformation(adminData);
        };

        const getExpanderTitle = (agreementType: string, adminData: AdminData): React.ReactElement => {
            const totalRoles = adminData.agreementTypes[agreementType].rolesData.roles;
            const selectedRoles = adminData.agreementTypes[agreementType].rolesData.roles.filter(
                (role) => role.isChecked
            );
            return (
                <span className="agreement-type-span" data-testid="agreement-type-span">
                    <span data-testid="agreement-type-span1">{message(`${agreementType}AccessRights`)}</span>
                    <span data-testid="agreement-type-span2">
                        ({selectedRoles.length}/{totalRoles.length} {message('rolesSelected')})
                    </span>
                </span>
            );
        };

        // @ts-ignore
        return (
            <div ref={ref}>
                <Tile className="admin-information-tile" data-testid="admin-information-tile">
                    <AdminInformationFormHeader {...{ enableDeleteButton, removeForm }} />
                    <Form visualizeRequiredFields data-testid="admin-information-tile-form">
                        <FormRow
                            label={message('firstName')}
                            disabled={isAdminFormDisabled}
                            required
                            data-testid="firstName"
                        >
                            <ValidInput
                                className={id.FIRST_NAME}
                                value={adminData.firstName}
                                maxLength={50}
                                data-testid="firstNameInput"
                                onChange={(event: any) => {
                                    adminData.firstName = event.target.value;
                                    updateInformation(adminData);
                                }}
                                error={!isAdminFormDisabled ? firstNameValidation.message : ''}
                                invalid={!isAdminFormDisabled && firstNameValidation.notValid}
                            />
                        </FormRow>
                        <FormRow
                            label={message('lastName')}
                            disabled={isAdminFormDisabled}
                            required
                            data-testid="lastName"
                        >
                            <ValidInput
                                className={id.LAST_NAME}
                                value={adminData.lastName}
                                maxLength={50}
                                data-testid="lastNameInput"
                                onChange={(event: any) => {
                                    adminData.lastName = event.target.value;
                                    updateInformation(adminData);
                                }}
                                error={!isAdminFormDisabled ? lastNameValidation.message : ''}
                                invalid={!isAdminFormDisabled && lastNameValidation.notValid}
                            />
                        </FormRow>
                        <FormRow
                            label={message('personalIdentityCode')}
                            disabled={isAdminFormDisabled}
                            required
                            data-testid="personalIdentityCode"
                        >
                            <ValidInput
                                className={id.PERSONAL_CODE}
                                value={adminData.personalIdentityCode}
                                data-testid="personalIdentityCodeInput"
                                onChange={(event: any) => {
                                    adminData.personalIdentityCode = event.target.value.toUpperCase();
                                    updateInformation(adminData);
                                }}
                                error={!isAdminFormDisabled ? identityNumberValidation.message : ''}
                                invalid={!isAdminFormDisabled && identityNumberValidation.notValid}
                            />
                        </FormRow>
                        <FormRow
                            label={message('phoneAdmin')}
                            disabled={isAdminFormDisabled}
                            required
                            data-testid="phoneAdmin"
                        >
                            <ValidInput
                                type="tel"
                                className={id.ADMIN_PHONE_NUMBER}
                                value={adminData.adminPhoneNumber}
                                maxLength={20}
                                data-testid="phoneAdminInput"
                                onChange={(event: any) => {
                                    adminData.adminPhoneNumber = event.target.value;
                                    updateInformation(adminData);
                                }}
                                error={!isAdminFormDisabled ? phoneNumberValidation.message : ''}
                                invalid={!isAdminFormDisabled && phoneNumberValidation.notValid}
                            />
                        </FormRow>
                        <FormRow
                            label={message('adminEmailAddress')}
                            disabled={isAdminFormDisabled}
                            required
                            data-testid="adminEmailAddress"
                        >
                            <ValidInput
                                className={id.EMAIL}
                                value={adminData.adminEmailAddress}
                                type="email"
                                maxLength={50}
                                data-testid="adminEmailAddressInput"
                                onChange={(event: any) => {
                                    adminData.adminEmailAddress = event.target.value;
                                    updateInformation(adminData);
                                }}
                                error={!isAdminFormDisabled ? emailValidation.message : ''}
                                invalid={!isAdminFormDisabled && emailValidation.notValid}
                            />
                        </FormRow>
                    </Form>
                    <Checkbox
                        aria-label={`${message('canEditRoles')} ${message('canEditRolesInfo')}`}
                        size="small"
                        checked={adminData.canEditRoles !== undefined ? Boolean(adminData.canEditRoles) : false}
                        labelProps={{ className: 'check-box-label' }}
                        data-testid="canEditRolesCheckbox"
                        onChange={(_e: any, checked: boolean) => {
                            adminData.canEditRoles = checked;
                            updateInformation(adminData);
                        }}
                    >
                        <div aria-hidden>
                            <p className="paragraph-no-padding-bottom">{message('canEditRoles')}</p>
                            <p className="disclaimer-font">{message('canEditRolesInfo')}</p>
                        </div>
                    </Checkbox>
                    <h3 className="opux-spacing-top-margin bold" data-testid="selectAdminAgreementTypes">
                        {message('selectAdminAgreementTypes')}
                    </h3>
                    {isAdminFlow && contractData.showAgreementTypes && <DigitalAgreementInformation />}
                    <p data-testid="adminAgreementTypesInfoText">{message('adminAgreementTypesInfoText')}</p>
                    <CheckboxGroup
                        size="small"
                        className="opux-spacing-bottom-margin-double"
                        data-testid="checkboxGroup"
                    >
                        <>
                            {getCompanyTypes(isAdminFlow, contractData.showAgreementTypes).map((type, index) => (
                                <Checkbox
                                    className={index === 0 ? id.SERVICE_CHECKBOX : ''}
                                    key={`companyType-${type}`}
                                    data-testid={`companyTypeCheckbox-${type}`}
                                    onChange={(_, checked) => updateAdminAgreements(type, checked)}
                                    checked={(adminData.agreementTypes as any)[type] !== undefined}
                                    invalid={!isAdminFormDisabled && adminsSelectedServices.notValid}
                                    aria-labelledby={`admin-${type}-checkbox`}
                                >
                                    <div id={`admin-${type}-checkbox`}>{message(type)}</div>
                                    {isAdminFlow && (
                                        <PopupHint className="agreement-type-info" data-testid={`popupHint-${type}`}>
                                            {message(`${type}InfoBox`)}
                                        </PopupHint>
                                    )}
                                </Checkbox>
                            ))}
                        </>
                        <InputError
                            error={!isAdminFormDisabled ? adminsSelectedServices.message : ''}
                            data-testid="inputError"
                        />
                    </CheckboxGroup>
                    {Object.keys(adminData.agreementTypes).length > 0 && (
                        <h3 className="opux-spacing-top-margin opux-spacing-bottom-margin" data-testid="adminRoles">
                            {message('adminRoles')}
                        </h3>
                    )}
                    <ExpanderList className="opux-spacing-top-margin" data-testid="expanderList">
                        {Object.keys(adminData.agreementTypes)
                            .sort(sortByAgreementType)
                            .map((agreementType) => (
                                <Expander
                                    indented
                                    key={`adminType-${agreementType}`}
                                    data-testid={`rolesExpander-${agreementType}`}
                                    title={getExpanderTitle(agreementType, adminData)}
                                    initiallyOpen={true}
                                    wrapContent={!isMobile}
                                >
                                    <p data-testid="adminRolesInfoText">{message('adminRolesInfoText')}</p>
                                    {isAdminFlow && agreementType === 'company' && (
                                        <Alert data-testid="adminFlowOrgUnitText">
                                            {message('adminFlowOrgUnitText')}
                                        </Alert>
                                    )}
                                    <Roles
                                        admin={adminData}
                                        updateAdminRoles={updateInformation}
                                        agreementType={agreementType}
                                    />
                                </Expander>
                            ))}
                    </ExpanderList>
                </Tile>
            </div>
        );
    }
);

AdminInformationForm.displayName = 'AdminInformationForm';

export default AdminInformationForm;
